import * as React from 'react';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { AmorgAdminToken, CompanyInfo, Partner, Site, SiteSettings } from "../../amorg-common";
import { getActiveUser, createSite, getAllSites, updateSiteSettings } from '../../api/adminClientApi';
import { ButtonRow } from '../../designSystem/button/ButtonRow';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { Table, TableRowWithOnClick } from '../../designSystem/Table';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { Button } from '../../designSystem/button/Button';
import { TextField } from '../../designSystem/formField/text/TextField';
import { Modal } from '../../designSystem/Modal';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { AmorgAdminPageProvider } from './AmorgAdminPageContext';
import { Icon } from '../../designSystem/icons/Icons';
import { colors } from '../../designSystem/colors/color';
import { OnOff } from '../../designSystem/inputs/OnOff';
import { AdminTab } from './_components/AdminTab';
import { ImpersonateModalButton } from './_components/ImpersonateModalButton';

export default function AmorgAdminSites (): JSX.Element 
{
    const history = useHistory();

    const [loading, setLoading] = React.useState(true);
    const [sites, setSites] = React.useState<Site[]>([]);
    const [activeUser, setActiveUser] = React.useState<AmorgAdminToken>(null);

    useEffect(() =>
    {
        const init = async () =>
        {
            const token = await getActiveUser(() => history.push(`/amorgy/login`));
            setActiveUser(token);
            setSites(await getSites());
            setLoading(false);
        };
        init().catch(console.error);
    }, []);

    async function getSites ()
    {
        return await getAllSites(() => history.push(`/amorgy/login`));
    }

    function handleChangeEnabled (siteId: string, settings: SiteSettings, partner: Partner | null, loginName:string)
    {
        return async (_: string, checked: boolean) => 
        {
            setLoading(true);
            const companyInfo = sites.find(s => s.id === siteId)?.companyInfo as CompanyInfo;
            await updateSiteSettings(siteId, { ...settings, enabled: checked }, companyInfo, partner, loginName, () => history.push('/amorgy/login'));
            setSites(await getSites());
            setLoading(false);
        };
    }

    return (
        <AmorgAdminPageProvider>
            <LoadingPageOverlay loading={loading} />
            <SingleColumnLayout
                noDivider
                headerContainer={
                    <Flex flexDirection="column" gap={16}>
                        <AdminTab selectedTab='sites' />
                        <ButtonRow>
                            <CreateSiteModalButton onCreated={async () => setSites(await getSites())} />
                        </ButtonRow>
                    </Flex>
                }

                contentContainer={

                    <Flex flexDirection='column' gap={16}>
                        <Table>
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Organisation</th>
                                    <th>Org. id</th>
                                    <th>
                                        {activeUser && activeUser.partner===null &&
                                            <>Partner</>
                                        }
                                    </th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sites.map(s =>
                                    <TableRowWithOnClick key={s.id} onClick={() => history.push(`/amorgy/sites/${s.id}`)}>
                                        <td>
                                            {s.settings.enabled
                                                ? <Icon name="status_ok" color={colors.icon.success} />
                                                : null
                                            }
                                        </td>
                                        <td style={{ textDecoration: s.settings.enabled ? 'none' : 'line-through' }}>
                                            {s.companyInfo?.name}
                                        </td>
                                        <td style={{ textDecoration: s.settings.enabled ? 'none' : 'line-through' }}>
                                            {s.id}
                                        </td>
                                        <td>
                                            {activeUser && activeUser.partner===null &&
                                                <>{s.partner || ''}</>
                                            }
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()} style={{ width: '0.1%', whiteSpace: 'nowrap' }}>
                                            <ButtonRow variant="right-aligned" style={{ alignItems: 'center' }}>
                                                <OnOff
                                                    active={s.settings.enabled}
                                                    onClick={(enabled) =>
                                                    {

                                                        handleChangeEnabled(s.id, s.settings, s.partner, s.loginName)('', enabled);
                                                    }} />
                                                <ImpersonateModalButton
                                                    siteId={s.id}
                                                    siteEnabled={s.settings.enabled} />
                                            </ButtonRow>

                                        </td>
                                    </TableRowWithOnClick>
                                )}
                            </tbody>
                        </Table>

                    </Flex>
                } />

        </AmorgAdminPageProvider>
    );
}

function CreateSiteModalButton (
    props: {
        onCreated: () => void;
    }): JSX.Element
{
    const history = useHistory();

    const [loading, setLoading] = React.useState(false);
    const [siteId, setSiteId] = React.useState('');
    const [error, setError] = React.useState(false);
    const [showModal, setShowModal] = React.useState(false);

    async function handleClickCreateSite ()
    {
        setLoading(true);
        const result = await createSite(siteId, () => history.push('/amorgy/login'));
        setLoading(false);
        setError(!result);
        if (result)
        {
            setShowModal(false);
            setSiteId('');
            props.onCreated();
        }
    }

    async function handleClickClose ()
    {
        setShowModal(false);
        setError(false);
        setSiteId('');
    }

    return (
        <>
            <LoadingPageOverlay loading={loading} />

            <Button icon="actions_plus" variant="filled" text="Opret nyt site" onClick={() => setShowModal(true)} />

            <Modal
                title={"Opret nyt site"}
                content={
                    <Flex flexDirection="column" gap={16}>
                        <TextField
                            value={siteId}
                            onChange={setSiteId}
                            label="Site ID/ Navn"
                            validator={() => (error 
                                ? { valid: false, errorMessage: 'Skal være mellem 2 og 32 karakterer. Må kun indeholde små bogstaver og tal, skal begynde med et bogstav, må ikke indeholde danske bogstaver og må ikke være et reserveret ord' } : 
                                { valid: true })} />
                        <Flex flexDirection="row" justifyContent="space-between">
                            <Button variant="text" text='Annullér'
                                onClick={handleClickClose} />
                            <Button variant="filled" text='Opret'
                                onClick={handleClickCreateSite} />
                        </Flex>
                    </Flex>
                }
                showModal={showModal}
                onCancel={handleClickClose} />


        </>
    );
}